class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';


    this.customSearchProducts = this.querySelector('custom-search-products');

    /*
     * AI-Fallback. Er hat einen eigenen AbortController, weil er einen eigenen
     * Lebenszyklus hat: die Vorschlagssuche ist längst beantwortet, wenn er
     * startet, und ihr Controller wird zwischendurch ersetzt.
     *
     * Der Timer ist der eigentliche Kostenschutz. Die Vorschlagssuche feuert bei
     * jedem Tastendruck, ein KI-Aufruf pro Buchstabe wäre teuer und käme immer
     * zu spät. Deshalb erst wenn getippt fertig ist.
     *
     * aiPrefetch hält die spekulativen Aufrufe, die schon parallel zur Wortsuche
     * losgehen (siehe onChange): Begriff -> laufendes Promise. Ohne sie stünde
     * die Antwortzeit des Workers komplett hinter der der Wortsuche, obwohl
     * beide dieselbe Eingabe haben und niemand aufeinander warten muss.
     */
    this.aiController = null;
    this.aiTimer = null;
    this.aiCache = {};
    this.aiPrefetch = {};

    this.setupEventListeners();
  }

  setupEventListeners() {
    // The submit listener comes from SearchForm, which also quotes the term.

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onProductClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);

    /*
     * Spekulativ und parallel zur Wortsuche, nicht erst nach ihr: bis deren
     * Antwort da ist (~450ms), hat ein Cache-HIT des Workers längst geantwortet
     * und ein MISS einen halben Vorsprung. Stellt sich heraus, dass die
     * Wortsuche genug gefunden hat, wird die Antwort einfach nie angezeigt -
     * sie liegt dann im aiCache für den Fall, dass jemand weitertippt und
     * wieder zurücklöscht.
     */
    this.prefetchAiSearch(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    super.onFormSubmit(event);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close(true);
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onProductClick(event) {
    const clickedElement = event.target.closest('[data-search-product-handle]');
    if (clickedElement) {
      const productHandle = clickedElement.getAttribute('data-search-product-handle');

      if (productHandle) {
        try {
          // Get existing list of product handles or create empty array
          let productHandlesList = JSON.parse(localStorage.getItem('BREKKY_Last_Searched_Product_Handles') || '[]');

          // Add new product handle to the beginning if it's not already there
          if (!productHandlesList.includes(productHandle)) {
            productHandlesList.unshift(productHandle);
            // Keep only the last 20 products
            productHandlesList = productHandlesList.slice(0, 20);
          } else {
            // If product already exists, move it to the front
            productHandlesList = productHandlesList.filter(handle => handle !== productHandle);
            productHandlesList.unshift(productHandle);
          }

          // Save the updated list
          localStorage.setItem('BREKKY_Last_Searched_Product_Handles', JSON.stringify(productHandlesList));
        } catch (error) {
          console.error('Failed to save product handle to localStorage:', error);
        }
      }
    }
  }

  extractHandleFromUrl(url) {
    if (!url) return null;

    // Extract handle from URL like /products/product-handle or /en/products/product-handle
    const match = url.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close(true);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close(true);
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', true);
  }


  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    const hasResults = resultsMarkup.includes('id="predictive-search-results-groups-wrapper"');
    if (hasResults) {
      this.customSearchProductsHide();
    } else {
      this.customSearchProductsShow();
    }
    this.setLiveRegionResults();
    this.open();

    /*
     * Das Markup oben hat die vorherigen KI-Treffer gerade mit weggeworfen, was
     * richtig ist - sie galten für den alten Suchbegriff. Blieb die Wortsuche
     * dünn, geht es von vorn los.
     *
     * Die Handles kommen aus dem Markup und nicht aus dem DOM: im DOM stehen
     * inzwischen auch die empfohlenen Produkte, die niemand gesucht hat. Was
     * hier gezählt wird, sind nur die Treffer der Wortsuche - und dieselbe Liste
     * sortiert später die Dopplungen aus.
     */
    this.aiKnownHandles = [
      ...new DOMParser()
        .parseFromString(resultsMarkup, 'text/html')
        .querySelectorAll('[data-search-product-handle]'),
    ].map((element) => element.dataset.searchProductHandle);

    if (this.aiKnownHandles.length < this.aiMinResults()) {
      this.scheduleAiSearch(this.searchTerm);
    } else {
      this.cancelAiSearch();
    }
  }

  /**
   * Ab wie wenigen Wortsuche-Treffern die KI ergänzt. 0 heißt: nur wenn gar
   * nichts gefunden wurde, also der alte Fallback. Ein fehlendes Attribut wird
   * genauso behandelt - lieber zu selten fragen als unbemerkt zu oft.
   */
  aiMinResults() {
    const configured = parseInt(this.dataset.aiMinResults, 10);
    return Number.isFinite(configured) ? Math.max(configured, 1) : 1;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  /*
   * ---- AI-Fallback -------------------------------------------------------
   *
   * Greift erst, wenn die Vorschlagssuche nichts gefunden hat. Sie sucht nach
   * Wörtern, der Worker nach Bedeutung - "worein mache ich meine Bowl" hat kein
   * Wort mit "Kokosnuss Bowl" gemeinsam, meint aber genau die.
   *
   * Jeder Fehlerweg endet gleich: die Gruppe wieder rausnehmen. Dann steht im
   * Dropdown wieder nur "nichts gefunden", und das ist eine korrekte Antwort.
   */

  /** Vier Zeilen. Mehr macht aus dem Dropdown eine zweite Ergebnisseite. */
  static get AI_MAX_RESULTS() {
    return 4;
  }

  /*
   * Was der Worker höchstens liefern soll. Mehr als AI_MAX_RESULTS, weil die
   * Wortsuche-Dopplungen noch rausfallen - aber deutlich weniger als die zwölf
   * der Ergebnisseite: die Antwortzeit des Modells hängt fast nur daran, wie
   * viele Handles es schreibt.
   */
  static get AI_FETCH_LIMIT() {
    return 6;
  }

  /*
   * So lange muss die Tastatur still sein, bevor ein Aufruf rausgeht. Nur noch
   * der Rückfallweg: normalerweise läuft der Aufruf seit onChange schon, und
   * dann wird gar nicht mehr entprellt.
   */
  static get AI_DEBOUNCE_MS() {
    return 250;
  }

  /** Derselbe Pfeil wie über den normalen Treffern, siehe snippets/icon-arrow.liquid. */
  static get ARROW_ICON() {
    return `<svg viewBox="0 0 14 10" fill="none" aria-hidden="true" focusable="false" class="icon icon-arrow" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"></path></svg>`;
  }

  cancelAiSearch() {
    clearTimeout(this.aiTimer);
    this.aiTimer = null;
    if (this.aiController) {
      this.aiController.abort();
      this.aiController = null;
    }
  }

  /**
   * Die URL zum Worker für einen Begriff - eine Stelle, damit der spekulative
   * Aufruf und der Rückfallweg garantiert denselben Cache-Eintrag treffen.
   */
  buildAiUrl(searchTerm) {
    const url = new URL(this.dataset.aiEndpoint);
    url.searchParams.set('query', searchTerm);
    url.searchParams.set('shop', this.dataset.aiShop);
    if (this.dataset.aiLanguage) url.searchParams.set('language', this.dataset.aiLanguage);
    url.searchParams.set('limit', String(PredictiveSearch.AI_FETCH_LIMIT));
    return url;
  }

  async fetchAiData(searchTerm, signal) {
    /*
     * Als String, nicht als URL-Objekt. Im Shop hängen mehrere Apps in
     * window.fetch und nehmen an, dass das erste Argument eine Zeichenkette
     * ist - eine von ihnen ruft darauf .includes() auf und wirft sonst
     * "requestURL.includes is not a function", noch bevor der Aufruf
     * rausgeht. fetch selbst wäre das URL-Objekt recht.
     */
    const response = await fetch(this.buildAiUrl(searchTerm).toString(), signal ? { signal } : undefined);
    // 404 ist die Antwort "nichts passt" - ein leeres Ergebnis, kein Fehler.
    if (!response.ok && response.status !== 404) {
      throw new Error(`search worker responded ${response.status}`);
    }

    const data = await response.json();
    return {
      handles: Array.isArray(data.handles) ? data.handles : [],
      // Titel, Bild und Preis, wenn der Worker sie schon mitliefert - dann
      // entfällt später der Section-Rendering-Roundtrip pro Treffer.
      results: Array.isArray(data.results) ? data.results : null,
    };
  }

  /**
   * Startet den Worker-Aufruf, ohne irgendetwas anzuzeigen. Ob die KI-Gruppe
   * überhaupt gebraucht wird, entscheidet erst renderSearchResults - aber die
   * Netzwerkzeit muss bis dahin nicht ungenutzt verstreichen.
   *
   * Ohne AbortController mit Absicht: eine spekulative Antwort ist auch dann
   * noch etwas wert, wenn längst weitergetippt wurde - sie liegt im aiCache
   * für den Backspace-Weg zurück zum selben Begriff.
   */
  prefetchAiSearch(searchTerm) {
    if (!this.dataset.aiEndpoint || !this.dataset.aiShop) return;

    const term = (searchTerm || '').trim();
    if (term.length < 3) return;
    if (this.aiCache[term] || this.aiPrefetch[term]) return;

    const request = this.fetchAiData(term, null)
      .then((data) => {
        this.aiCache[term] = data;
        return data;
      })
      .finally(() => {
        delete this.aiPrefetch[term];
      });
    // Scheitern ist hier folgenlos - der sichtbare Weg holt sich seinen
    // eigenen Fehler, wenn es so weit kommt.
    request.catch(() => {});
    this.aiPrefetch[term] = request;
  }

  scheduleAiSearch(searchTerm) {
    this.cancelAiSearch();

    // Ohne data-ai-endpoint ist die Funktion im Customizer aus, siehe
    // snippets/ai-search-attributes.liquid.
    if (!this.dataset.aiEndpoint || !this.dataset.aiShop) return;

    // Zwei Buchstaben beschreiben nichts, wofür sich ein Modell lohnt.
    const term = (searchTerm || '').trim();
    if (term.length < 3) return;

    /*
     * Der Hinweis steht sofort da, nicht erst wenn der Aufruf rausgeht. Sonst
     * liegt zwischen dem letzten Buchstaben und dem ersten sichtbaren Zeichen
     * das Debounce plus die Antwortzeit des Modells, und in der Zeit sieht es
     * aus, als wäre die Suche bei "nichts gefunden" fertig.
     */
    this.renderAiGroup();

    /*
     * Liegt die Antwort schon da oder ist der spekulative Aufruf unterwegs,
     * gibt es nichts mehr zu entprellen - das Debounce würde nur eine fertige
     * Antwort aufhalten.
     */
    if (this.aiCache[term] || this.aiPrefetch[term]) {
      this.runAiSearch(term);
      return;
    }

    this.aiTimer = setTimeout(() => this.runAiSearch(term), PredictiveSearch.AI_DEBOUNCE_MS);
  }

  async runAiSearch(searchTerm) {
    // Steht seit scheduleAiSearch schon da; neu gebaut wird nur, was fehlt.
    const group = this.querySelector('#predictive-search-ai') || this.renderAiGroup();
    if (!group) return;

    this.aiController = new AbortController();
    const signal = this.aiController.signal;

    let data = this.aiCache[searchTerm];

    // Der spekulative Aufruf läuft noch - warten statt doppelt anfragen.
    if (!data && this.aiPrefetch[searchTerm]) {
      data = await this.aiPrefetch[searchTerm].catch(() => null);
    }

    if (!data) {
      try {
        data = await this.fetchAiData(searchTerm, signal);
        this.aiCache[searchTerm] = data;
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.warn('AI search unavailable', error);
        return this.giveUpAiGroup();
      }
    }

    if (signal.aborted) return;

    /*
     * Der Worker kennt die Wortsuche nicht und schlägt ihre Treffer oft mit vor.
     * Dasselbe Produkt zweimal untereinander sieht nach kaputt aus, also bleibt
     * hier nur, was oben noch nicht steht.
     */
    const known = this.aiKnownHandles || [];
    const enriched =
      data.results && data.results.length
        ? data.results
        : data.handles.map((handle) => ({ handle }));
    const fresh = enriched.filter((result) => !known.includes(result.handle));

    // Der einzige Weg, auf dem wirklich feststeht, dass es nichts gibt.
    if (!fresh.length) return this.giveUpAiGroup({ empty: !data.handles.length });

    const top = fresh.slice(0, PredictiveSearch.AI_MAX_RESULTS);

    /*
     * Bringt die Antwort Titel, Bild und Preis schon mit, entstehen die Zeilen
     * direkt hier - das spart den zweiten Roundtrip über das Section Rendering
     * (~400ms), der vorher zwischen Antwort und sichtbarem Treffer lag. Der
     * Section-Weg bleibt als Rückfall für eine ältere Worker-Version und für
     * den Fall, dass der Händler den Hersteller einblendet - den kennt nur
     * das Theme.
     */
    let rows;
    if (this.canBuildAiRowsLocally(top)) {
      rows = top.map((result) => this.buildAiRow(result));
    } else {
      rows = await Promise.all(top.map((result) => this.fetchAiRow(result.handle, signal)));
      // Zwischen dem ersten fetch und hier kann längst weitergetippt worden sein.
      if (signal.aborted || !this.isConnected) return;
    }

    const rendered = rows.filter(Boolean);
    if (!rendered.length) return this.giveUpAiGroup();

    // fresh.length und nicht rendered.length: der Link soll sagen, wie viel es
    // insgesamt gibt, nicht wie viele Karten hier hineinpassten.
    this.fillAiGroup(group, rendered, { total: fresh.length, searchTerm });
  }

  canBuildAiRowsLocally(results) {
    if (this.dataset.aiShowVendor === 'true') return false;
    return results.every((result) => result.title && 'image' in result);
  }

  /**
   * Eine Dropdown-Zeile, gebaut wie sections/ai-predictive-item.liquid sie
   * rendert - dieselben Klassen, damit sie neben den nativen Treffern nicht
   * als Fremdkörper auffällt. Über DOM-Elemente statt Markup-Strings, damit
   * ein Produkttitel mit Sonderzeichen Text bleibt.
   */
  buildAiRow({ handle, title, image, price }) {
    const root = window.Shopify?.routes?.root || '/';

    const link = document.createElement('a');
    link.setAttribute('data-search-product-handle', handle);
    link.href = `${root}products/${encodeURIComponent(handle)}`;
    link.className =
      'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
    link.tabIndex = -1;

    if (image) {
      const img = document.createElement('img');
      img.className = 'predictive-search__image';
      img.src = image;
      img.alt = '';
      img.width = 70;
      link.append(img);
    }

    const showPrice = this.dataset.aiShowPrice === 'true' && price;
    const content = document.createElement('div');
    content.className = `predictive-search__item-content${
      showPrice ? '' : ' predictive-search__item-content--centered'
    }`;

    const heading = document.createElement('p');
    heading.className = 'predictive-search__item-heading h5';
    heading.textContent = title;
    content.append(heading);

    if (showPrice) {
      const priceElement = document.createElement('div');
      priceElement.className = 'price';
      priceElement.textContent = this.formatAiPrice(price);
      content.append(priceElement);
    }

    link.append(content);
    return link.outerHTML;
  }

  formatAiPrice(price) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: price.currencyCode,
      }).format(Number(price.amount));
    } catch (error) {
      return `${price.amount} ${price.currencyCode}`;
    }
  }

  async fetchAiRow(handle, signal) {
    const url = `${window.Shopify.routes.root}products/${encodeURIComponent(
      handle
    )}?section_id=ai-predictive-item`;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) return null;

      const markup = await response.text();
      const section = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('#shopify-section-ai-predictive-item');

      return section ? section.innerHTML : null;
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn(`AI search: row ${handle} failed`, error);
      return null;
    }
  }

  /**
   * Legt die Gruppe mit Spinner und Statuszeile an. Der Spinner ist derselbe wie
   * im restlichen Theme, damit das Warten nicht nach Fremdkörper aussieht.
   *
   * Zwei Plätze, je nachdem was die Wortsuche geliefert hat: hinter den "nichts
   * gefunden"-Satz, oder unter die gefundenen Produkte und noch vor die
   * empfohlenen - die stehen für sich und sind keine Antwort auf die Suche.
   *
   * Die Überschrift kommt bewusst erst mit den Treffern. Über einem leeren
   * Kasten verspricht sie etwas, das es noch nicht gibt.
   */
  renderAiGroup() {
    this.removeAiGroup();

    const group = document.createElement('div');
    group.id = 'predictive-search-ai';
    group.className = 'predictive-search__result-group';
    group.setAttribute('loading', 'true');
    group.innerHTML = `
      <p class="predictive-search__ai-status" role="status">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
        <span>${this.dataset.aiLoading || ''}</span>
      </p>
    `;

    /*
     * Der "keine Ergebnisse, überprüfe die Schreibweise"-Satz geht schon hier weg
     * und nicht erst bei den Treffern. Während gesucht wird, ist er verfrüht: er
     * schickt jemanden zum Korrigieren, während unter ihm die Antwort entsteht.
     * Findet die KI nichts, kommt er in giveUpAiGroup zurück.
     */
    const noResults = this.querySelector('#predictive-search-no-results');
    if (noResults) {
      noResults.style.display = 'none';
      this.collapseEmptyResultsWrapper(true);
    }

    /*
     * Hat die Wortsuche Produkte gefunden, gehören die ergänzten in dieselbe
     * Spalte, direkt darunter - es ist eine Liste, keine zweite Fundstelle.
     * Innerhalb der Gruppe und nicht als eigene: eigene Gruppen sind Spalten
     * dieser Reihe, und ab 750px läuft sie row-reverse.
     */
    const productsGroup = this.querySelector('#predictive-search-products')?.closest(
      '.predictive-search__result-group'
    );
    if (productsGroup) {
      productsGroup.append(group);
      return group;
    }

    /*
     * Sonst unter die Gruppen-Zeile, nicht hinein. Drinnen wäre die KI eine
     * weitere Spalte, und bei "--no-products" blendet das Theme deren zweites
     * Kind ab 750px aus - genau unsere Gruppe. Am Handy fiel das nie auf, weil
     * die Reihe dort eine Spalte ist.
     */
    const wrapper = this.querySelector('#predictive-search-results-groups-wrapper');
    if (wrapper) {
      wrapper.insertAdjacentElement('afterend', group);
      return group;
    }

    if (!this.predictiveSearchResults) return null;
    this.predictiveSearchResults.append(group);
    return group;
  }

  /**
   * Der Link auf die Ergebnisseite, wenn dort mehr steht als hier.
   *
   * Ohne Anführungszeichen um den Suchbegriff - anders als der Link über den
   * normalen Treffern. Die Anführungszeichen lösen auf der Ergebnisseite die
   * exakte Suche aus, die bei einer beschriebenen Frage nichts findet und erst
   * über eine Weiterleitung zur lockeren Suche kommt. Die KI-Treffer stehen
   * ohnehin nicht in den Wörtern; der Umweg kostet nur eine Ladung.
   */
  aiViewAllLink(searchTerm) {
    const label = this.dataset.aiViewAll;
    if (!label) return '';

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}search?q=${encodeURIComponent(
      searchTerm
    )}&options%5Bprefix%5D=last&type=product,article`;

    return `<a href="${url}" tabindex="-1" role="option" aria-selected="false"><span>${label}</span>${PredictiveSearch.ARROW_ICON}</a>`;
  }

  fillAiGroup(group, rows, { total = rows.length, searchTerm = '' } = {}) {
    const items = (rows, prefix, offset = 0) =>
      rows
        .map(
          (row, index) =>
            `<li id="predictive-search-option-${prefix}-${offset + index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">${row}</li>`
        )
        .join('');

    /*
     * Gibt es schon eine Trefferliste, wandern die Zeilen einfach hinein. Eine
     * eigene Überschrift dazwischen machte aus einer Liste zwei und ließ das
     * gefundene Produkt aussehen, als gehöre es nicht dazu - dabei ist es
     * dieselbe Antwort auf dieselbe Frage, nur unterschiedlich gefunden.
     *
     * Die ids zählen hinter den vorhandenen weiter, damit keine doppelt vergeben
     * wird; die Tastatursteuerung geht über sie.
     */
    const list = this.querySelector('#predictive-search-results-products-list');
    if (list) {
      list.insertAdjacentHTML('beforeend', items(rows, 'product', list.children.length));
      group.remove();
      return;
    }

    /*
     * Der Worker findet mehr, als hier hineinpasst - vier Zeilen sind ein
     * Vorschlag, keine Trefferliste. Wer mehr will, kommt über denselben Link
     * weiter wie bei den normalen Treffern, nur dass die Ergebnisseite die
     * KI-Treffer dort ins Raster legt.
     */
    const viewAll = total > rows.length ? this.aiViewAllLink(searchTerm) : '';

    group.removeAttribute('loading');
    group.innerHTML = `
      <h2 id="predictive-search-ai-heading" class="predictive-search__heading text-body caption-with-letter-spacing">${
        this.dataset.aiHeading || ''
      }${viewAll}</h2>
      <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-ai-heading">
        ${items(rows, 'ai')}
      </ul>`;
  }

  /**
   * Aufgeben: die Gruppe raus, der Hinweis zurück.
   *
   * `empty` heißt, der Worker hat geantwortet und kannte nichts - dann ist der
   * kurze Satz die ganze Wahrheit, und den langen Standardsatz mit dem eigenen
   * Suchbegriff darin braucht niemand vorgelesen. Bei einem Fehler bleibt der
   * Standardsatz stehen: der rät zum Umformulieren, und das ist dann der
   * richtige Rat.
   */
  giveUpAiGroup({ empty = false } = {}) {
    const noResults = this.querySelector('#predictive-search-no-results');
    if (noResults) {
      noResults.style.display = '';
      this.collapseEmptyResultsWrapper(false);

      const text = empty ? this.dataset.aiEmpty : null;
      const line = noResults.querySelector('.predictive-search__no-results-text');
      if (text && line) line.textContent = text;
    }

    this.removeAiGroup();
  }

  removeAiGroup() {
    this.querySelector('#predictive-search-ai')?.remove();
  }

  /**
   * Der Kasten der Wortsuche bringt Innenabstand und eine Linie unten mit. Ist
   * der versteckte "nichts gefunden"-Satz alles, was noch darin steht, bleibt
   * zwischen Suchfeld und Statuszeile ein leerer Streifen mit zwei Strichen
   * stehen - er sieht aus, als wäre dort etwas kaputt gegangen.
   *
   * Nur wenn wirklich nichts mehr sichtbar ist - was sonst noch im Kasten
   * liegt, etwa die Empfehlungen, ist Inhalt und hält ihn offen.
   */
  collapseEmptyResultsWrapper(collapsed) {
    const wrapper = this.querySelector('#predictive-search-results-groups-wrapper');
    const noResults = wrapper?.querySelector('#predictive-search-no-results');
    if (!wrapper || !noResults) return;

    if (!collapsed) {
      wrapper.style.display = '';
      return;
    }

    const stillVisible = [...wrapper.children].some(
      (child) => child !== noResults && getComputedStyle(child).display !== 'none'
    );

    if (!stillVisible) wrapper.style.display = 'none';
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // close() hides the results so the recent-products list can take over. Focusing the
    // input again reuses cached results and skips renderSearchResults, so nothing would
    // undo that and the matches stayed invisible - restore the visibility here.
    this.predictiveSearchResults.style.display = this.querySelector('#predictive-search-results-groups-wrapper')
      ? 'block'
      : 'none';
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.customSearchProductsShow();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  customSearchProductsShow() {
    if (!this.customSearchProducts) return;

    this.customSearchProducts.customSearchProductsShow();
    this.querySelector('.search-modal__form').append(this.customSearchProducts);
    this.predictiveSearchResults.style.display = 'none';
  }

  customSearchProductsHide() {
    if (!this.customSearchProducts) return;

    const resultsWrapper = this.querySelector('#predictive-search-results-groups-wrapper');
    if (!resultsWrapper) return;

    this.customSearchProducts.customSearchProductsHide();
    resultsWrapper.append(this.customSearchProducts);
    this.predictiveSearchResults.style.display = 'block';
  }
}

customElements.define('predictive-search', PredictiveSearch);
